import { useFormContext } from 'react-hook-form';
import { WizardSummary } from '@/components/wizards';
import type { FieldSchema } from '@/hooks/useInsuranceTypeSchema';
import {
  GENERIC_BUSINESS_TYPE_OPTIONS,
  dynamicFields,
  humanizeSection,
  orderedSections,
} from '../../genericSchema';
import { isPremiumOptionBlank, type PremiumOptionDraft } from '../../premiumOptions';
import { formatCurrency } from '@/utils';

interface GenericReviewStepProps {
  insuranceTypeLabel: string;
  fields: FieldSchema[];
  premiumOptions: PremiumOptionDraft[];
  groupLabel?: string;
  subgroupLabel?: string;
}

function labelFor(options: { value: string; label: string }[], value?: string): string {
  return options.find((o) => o.value === value)?.label || value || '-';
}

/**
 * Review for the generic flow. Everything shown here is derived from the same
 * resolved field schema that produced the steps, so a type whose schema an
 * admin extends tomorrow gets the extra rows on this screen for free.
 */
export function GenericReviewStep({
  insuranceTypeLabel,
  fields,
  premiumOptions,
  groupLabel,
  subgroupLabel,
}: GenericReviewStepProps) {
  const { watch } = useFormContext();
  const data = watch() as Record<string, string | undefined>;

  const renderValue = (field: FieldSchema): string => {
    const raw = (data[field.key] ?? '').toString().trim();
    if (!raw) return '-';
    if (field.data_type === 'boolean') return raw === 'yes' ? 'Yes' : 'No';
    if (field.data_type === 'choice') return labelFor(field.choices ?? [], raw);
    return raw;
  };

  const sections = [
    {
      title: 'Quote',
      items: [
        { label: 'Insurance Type', value: insuranceTypeLabel || '-' },
        {
          label: 'Business Type',
          value: labelFor(GENERIC_BUSINESS_TYPE_OPTIONS, data.business_type),
        },
        { label: 'Group', value: groupLabel || '-' },
        { label: 'Sub-group', value: subgroupLabel || '-' },
      ],
    },
    {
      title: 'Customer',
      items: [
        { label: 'Name', value: data.customer_name || '-' },
        { label: 'Phone', value: data.customer_phone || '-' },
        { label: 'Email', value: data.customer_email || '-' },
      ],
    },
  ];

  for (const section of orderedSections(fields)) {
    const items = dynamicFields(fields)
      .filter((f) => f.section === section)
      .map((f) => ({ label: f.label, value: renderValue(f) }));
    if (items.length) sections.push({ title: humanizeSection(section), items });
  }

  const filledOptions = premiumOptions.filter((o) => !isPremiumOptionBlank(o));
  sections.push({
    title: `Premium Options (${filledOptions.length})`,
    items: filledOptions.length
      ? filledOptions.map((o, i) => ({
          label: `Option ${i + 1}`,
          value: [
            o.insurer_name || 'Insurer',
            o.product_name || 'Product',
            o.total_premium ? formatCurrency(o.total_premium) : '-',
            o.valid_until ? `expires ${o.valid_until}` : '',
            o.file ? `· ${o.file.name}` : '',
          ]
            .filter(Boolean)
            .join(' — '),
        }))
      : [{ label: 'Premium options', value: 'None added — you can add them later from the quote.' }],
  });

  if (data.notes && data.notes.trim()) {
    sections.push({ title: 'Notes', items: [{ label: 'Notes', value: data.notes.trim() }] });
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review &amp; Submit</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Check everything below, then submit to create the quote.
      </p>
      <WizardSummary sections={sections} />
    </div>
  );
}

export default GenericReviewStep;
